/**
 * CabinTrungLichPage — cảnh báo lịch cabin nên hủy, để người duyệt tự quyết.
 *
 * Màn này CỐ Ý không có nút "hủy tất cả". Đo trên dữ liệu thật: 91,9% học viên
 * chỉ học một ca, nhưng 7,6% học hai ca và trong số đó có người **vắng ca cũ
 * nên đăng ký ca mới** — họ chưa học buổi nào. Luật ở backend đã trừ ca vắng
 * (loại được 6 cảnh báo sai trong 28 khi đo lần đầu), nhưng một luật đúng 92%
 * vẫn không đủ để hủy thay người. Nên ở đây mỗi dòng là một nút riêng, và cột
 * lý do nói rõ vì sao dòng đó có mặt.
 *
 * Việc hủy đi qua đúng `calcomService.cancelBooking` mà trang cabin vẫn dùng,
 * nên policy hủy ở backend áp dụng nguyên vẹn — màn này không có đường tắt nào.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Badge, Button, Card, Container, Form, Modal, Spinner, Table,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import calcomService from '../services/calcom';
import AdminLogoutButton from '../components/AdminLogoutButton';
import CopyableText from '../components/CopyableText';
import { coQuyenTraCuuHocVienHienTai } from '../utils/quyenTraCuu';
import { nhanCa } from '../utils/nhanCaCabin';

/**
 * Trang tra cứu nhận `name`, và BE khớp `$or` cả họ tên lẫn `registrationCode`
 * — nên dán thẳng mã học viên vào đây là ra đúng một người.
 */
const linkTraCuu = (maHocVien) => (
  // `hasSession=0` + `hang=` TAT hai bo loc mac dinh cua trang tra cuu (chi HV
  // da co phien DAT, va chi hang BSS/BTD/C1). Do tren 23 ung vien that: de mac
  // dinh thi 2 nguoi CO trong dat_students van ra rong, va nguoi duyet tuong
  // ho so khong ton tai. Tat bo loc thi 23/23 ra ket qua.
  `/admin/session/student?name=${encodeURIComponent(maHocVien || '')}&hasSession=0&hang=`
);

const LY_DO = {
  da_hoc_du: {
    nhan: 'Đã học rồi',
    bg: 'warning',
    giaiThich: 'Học viên đã có ca cabin thực học (không tính ca vắng) mà vẫn giữ lịch này.',
  },
  giu_nhieu_lich: {
    nhan: 'Giữ nhiều lịch',
    bg: 'info',
    giaiThich: 'Học viên đang giữ nhiều lịch sắp tới cùng lúc. Đề nghị giữ lịch sớm nhất, hủy các lịch sau.',
  },
};

export default function CabinTrungLichPage() {
  const [dangTai, setDangTai] = useState(false);
  const [kq, setKq] = useState(null);
  const [cabinLoc, setCabinLoc] = useState('');
  const [dangHuy, setDangHuy] = useState(() => new Set());
  const [daHuy, setDaHuy] = useState(() => new Set());
  // Màn này dùng AdminDrivingGuard (có giáo viên + reporter), còn trang tra cứu
  // dùng AdminGuard (không có hai vai đó). Hiện nút cho họ là dẫn tới trang
  // đăng nhập admin — ngõ cụt. Đọc một lần, không đổi giữa phiên.
  const coTheTraCuu = useMemo(() => coQuyenTraCuuHocVienHienTai(), []);
  // Dòng đang mở modal chi tiết. Giữ cả dòng chứ không chỉ `bookingUid`: modal
  // cần tên, khoá, CCCD, lí do và `cacCa` — tất cả đã có sẵn trong dòng, nên
  // mở modal là tức thì, không gọi thêm API nào.
  const [chiTiet, setChiTiet] = useState(null);

  const tai = useCallback(async (serviceKey) => {
    setDangTai(true);
    try {
      const res = await calcomService.getLichCanHuy(serviceKey || undefined);
      const payload = res?.data;
      // Request thanh cong ma khong co payload nghia la hinh dang tra ve khac
      // voi mong doi — KHONG duoc de no thanh mot trang trong im lang. Da vap:
      // service boc `.data` mot lan thua, trang nhan undefined, man hinh trong
      // va khong co loi nao de lan ra nguyen nhan.
      if (!payload || !Array.isArray(payload.ungVien)) {
        toast.error('May chu tra ve du lieu khong doc duoc — bao ky thuat kiem lai.');
        setKq(null);
        return;
      }
      setKq(payload);
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setDangTai(false);
    }
  }, []);

  useEffect(() => { tai(); }, [tai]);

  const huy = useCallback(async (dong) => {
    const xacNhan = window.confirm(
      `Hủy lịch ngày ${dong.ngayLich} của học viên ${dong.hoTen || dong.maHocVien}?\n\n`
      + `Mã: ${dong.maHocVien}${dong.khoaHoc ? ` · Khoá ${dong.khoaHoc}` : ''}\n`
      + `Lý do gắn cờ: ${LY_DO[dong.lyDo]?.nhan || dong.lyDo}\n`
      + `Đã học: ${dong.soCaDaHoc} ca${dong.ngayCaCuoi ? ` (ca cuối ${dong.ngayCaCuoi})` : ''}\n\n`
      + 'Học viên sẽ mất suất này.',
    );
    if (!xacNhan) return;

    setDangHuy((s) => new Set(s).add(dong.bookingUid));
    try {
      await calcomService.cancelBooking(
        dong.bookingUid,
        'Hủy theo cảnh báo trùng lịch: học viên đã học đủ ca cabin',
        dong.cabin,
      );
      setDaHuy((s) => new Set(s).add(dong.bookingUid));
      toast.success('Đã hủy lịch');
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message);
    } finally {
      setDangHuy((s) => {
        const n = new Set(s);
        n.delete(dong.bookingUid);
        return n;
      });
    }
  }, []);

  const rows = useMemo(() => {
    const ds = kq?.ungVien || [];
    // Sắp theo KHOẢNG CÁCH giảm dần, không theo ngày lịch. Cờ vắng không đáng tin
    // tuyệt đối (có lúc giáo viên quên đánh), nên dòng cách càng xa càng chắc là
    // bỏ sót thật — đo thật thì 16/22 dòng cách 31–60 ngày. Đưa chúng lên đầu để
    // người duyệt xử những ca rõ ràng trước.
    return [...ds].sort((a, b) => {
      const x = a.soNgayCach ?? -1;
      const y = b.soNgayCach ?? -1;
      if (x !== y) return y - x;
      return String(a.ngayLich).localeCompare(String(b.ngayLich));
    });
  }, [kq]);

  return (
    <Container fluid className='p-3'>
      <div className='d-flex align-items-center justify-content-between mb-3'>
        <h5 className='mb-0'>Cảnh báo lịch cabin trùng</h5>
        <div className='d-flex gap-2'>
          <Button size='sm' variant='outline-secondary' href='/cabin'>Lịch cabin</Button>
          <AdminLogoutButton size='sm' />
        </div>
      </div>

      <Card className='mb-3'>
        <Card.Body className='d-flex flex-wrap gap-3 align-items-end'>
          <div>
            <Form.Label className='fw-semibold mb-1'>Cabin</Form.Label>
            <Form.Select
              value={cabinLoc}
              onChange={(e) => { setCabinLoc(e.target.value); tai(e.target.value); }}
              style={{ minWidth: 220 }}
            >
              <option value=''>Tất cả cabin đang bật cảnh báo</option>
              {(kq?.cabins || []).map((c) => (
                <option key={c.serviceKey} value={c.serviceKey}>{c.name}</option>
              ))}
            </Form.Select>
          </div>
          <Button variant='outline-primary' onClick={() => tai(cabinLoc)} disabled={dangTai}>
            {dangTai ? <Spinner size='sm' animation='border' className='me-2' /> : null}
            Tải lại
          </Button>
        </Card.Body>
      </Card>

      {dangTai && !kq ? (
        <div className='text-center py-5'>
          <Spinner animation='border' />
          <div className='text-muted mt-2'>Đang đọc lịch từ Cal.com…</div>
        </div>
      ) : null}

      {kq ? (
        <>
          {/* Cabin lỗi phải hiện rõ: im lặng thì màn hình hiện "ít lịch cần hủy"
            * và người duyệt tưởng cơ sở đó sạch. */}
          {kq.loi?.length ? (
            <Alert variant='danger'>
              Không đọc được lịch của {kq.loi.length} cabin, nên danh sách dưới đây <strong>chưa đủ</strong>:
              <ul className='mb-0 mt-1'>
                {kq.loi.map((l) => <li key={l.serviceKey}>{l.name}: {l.message}</li>)}
              </ul>
            </Alert>
          ) : null}
          {(kq.cabins || []).some((c) => c.nghiConSot) ? (
            <Alert variant='warning'>
              Có cabin trả về đúng số bản ghi tối đa mỗi trang — danh sách có thể còn sót.
            </Alert>
          ) : null}

          <Alert variant='light' className='border d-flex flex-wrap gap-3 align-items-center'>
            <span><strong>{kq.tong.soLichDeNghiHuy}</strong> lịch đề nghị hủy</span>
            <span>của <strong>{kq.tong.soHocVien}</strong> học viên</span>
            <span className='text-muted'>trên tổng {kq.tong.soLichSapToi} lịch sắp tới</span>
            <span className='text-muted'>· ngưỡng {kq.tong.nguongCa} ca đã học</span>
            {/* Trang này đọc BẢN SAO đồng bộ từ Cal.com, không gọi Cal.com trực
              * tiếp nữa (trước đó mất 23,6 giây mỗi lần mở). Nên phải nói rõ đang
              * nhìn ảnh chụp lúc nào — nhất là ngay sau khi ai đó vừa đặt lịch. */}
            <span className='text-muted'>
              · dữ liệu lúc{' '}
              {kq.capNhatLuc
                ? new Date(kq.capNhatLuc).toLocaleString('sv-SE', { timeZone: 'Asia/Ho_Chi_Minh' })
                : 'chưa đồng bộ lần nào'}
            </span>
          </Alert>

          <Alert variant='info' className='py-2'>
            Đây là <strong>cảnh báo để người duyệt đọc</strong>, không có gì tự hủy. Ca bị đánh
            dấu <strong>vắng</strong> không tính là đã học, nên học viên vắng ca cũ rồi đăng ký
            ca mới sẽ không xuất hiện ở đây.
            <div className='mt-1'>
              Nhưng việc đánh vắng <strong>không phải lúc nào cũng được làm</strong>, nên một ca
              không ai đến vẫn có thể đếm là đã học. Cột <strong>Cách</strong> là chỗ dựa: cách
              càng xa thì càng chắc là lịch bỏ quên chứ không phải người đang học dở.
            </div>
          </Alert>

          <Card>
            <div className='table-responsive' style={{ maxHeight: '65vh', overflow: 'auto' }}>
              <Table hover size='sm' className='mb-0 align-middle'>
                <thead className='table-light' style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                  <tr>
                    <th>Mã học viên</th>
                    <th>Học viên</th>
                    <th>Khoá</th>
                    <th>CCCD</th>
                    <th>Lý do</th>
                    <th className='text-center'>Đã học</th>
                    <th>Ca cuối</th>
                    <th className='text-end'>Cách</th>
                    <th>Lịch đang giữ</th>
                    <th>Cabin</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((d) => {
                    const ly = LY_DO[d.lyDo] || { nhan: d.lyDo, bg: 'secondary', giaiThich: '' };
                    const xong = daHuy.has(d.bookingUid);
                    return (
                      <tr key={d.bookingUid} className={xong ? 'table-secondary' : undefined}>
                        <td className='fw-semibold'>
                          <CopyableText value={d.maHocVien} title='Sao chép mã học viên' />
                        </td>
                        {/* Tra không ra hồ sơ thì để gạch ngang — dòng cảnh báo KHÔNG
                          * được biến mất chỉ vì thiếu tên. */}
                        <td>{d.hoTen || <span className='text-muted'>—</span>}</td>
                        <td className='small'>{d.khoaHoc || <span className='text-muted'>—</span>}</td>
                        <td className='small'>
                          {d.cccd
                            ? <CopyableText value={d.cccd} title='Sao chép CCCD' />
                            : <span className='text-muted'>—</span>}
                        </td>
                        <td>
                          <Badge bg={ly.bg} text={ly.bg === 'warning' ? 'dark' : undefined} title={ly.giaiThich}>
                            {ly.nhan}
                          </Badge>
                        </td>
                        <td className='text-center'>{d.soCaDaHoc}</td>
                        <td>{d.ngayCaCuoi || '—'}</td>
                        <td className='text-end'>
                          {d.soNgayCach === null || d.soNgayCach === undefined ? (
                            <span className='text-muted'>—</span>
                          ) : (
                            /* Càng xa càng chắc là bỏ sót thật chứ không phải đang học dở. */
                            <span className={d.soNgayCach > 30 ? 'fw-semibold' : 'text-muted'}>
                              {d.soNgayCach} ngày
                            </span>
                          )}
                        </td>
                        <td>{d.ngayLich}</td>
                        <td className='small text-muted'>{d.cabin}</td>
                        <td className='text-end text-nowrap'>
                          <Button
                            size='sm'
                            variant='outline-primary'
                            className='me-2'
                            onClick={() => setChiTiet(d)}
                          >
                            Chi tiết
                          </Button>
                          {coTheTraCuu ? (
                            /* Mở TAB MỚI: người duyệt đang ở giữa một danh sách, điều hướng
                             * ngay trên tab này là mất chỗ đang đứng. */
                            <Button
                              size='sm'
                              variant='outline-secondary'
                              className='me-2'
                              href={linkTraCuu(d.maHocVien)}
                              target='_blank'
                              rel='noreferrer'
                            >
                              Tra cứu
                            </Button>
                          ) : null}
                          {xong ? (
                            <span className='text-muted small'>Đã hủy</span>
                          ) : (
                            <Button
                              size='sm'
                              variant='outline-danger'
                              disabled={dangHuy.has(d.bookingUid)}
                              onClick={() => huy(d)}
                            >
                              {dangHuy.has(d.bookingUid)
                                ? <Spinner size='sm' animation='border' />
                                : 'Hủy lịch'}
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {!rows.length ? (
                    <tr>
                      <td colSpan={11} className='text-center text-muted py-4'>
                        {/* "Bảng rỗng" và "chưa đồng bộ lần nào" là hai chuyện
                          * khác nhau. Gộp làm một là để người duyệt yên tâm sai. */}
                        {kq.capNhatLuc
                          ? 'Không có lịch nào cần hủy.'
                          : 'Chưa đồng bộ lịch từ Cal.com lần nào — đây không phải “không có lịch nào cần hủy”.'}
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>
            </div>
          </Card>
        </>
      ) : null}

      {/* Chi tiết lịch cabin — CHỈ ĐỌC. Muốn hủy thì đóng modal rồi bấm ở dòng,
        * để chỉ có ĐÚNG MỘT đường hủy đi qua đúng policy đang có. */}
      <Modal show={!!chiTiet} onHide={() => setChiTiet(null)} size='lg' centered>
        <Modal.Header closeButton>
          <Modal.Title as='div'>
            <div className='h5 mb-0'>{chiTiet?.hoTen || chiTiet?.maHocVien}</div>
            <div className='small text-muted'>
              {chiTiet?.maHocVien}
              {chiTiet?.khoaHoc ? ` · Khoá ${chiTiet.khoaHoc}` : ''}
              {chiTiet?.cccd ? ` · CCCD ${chiTiet.cccd}` : ''}
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {chiTiet ? (
            <>
              <div className='mb-3'>
                <Badge
                  bg={(LY_DO[chiTiet.lyDo] || {}).bg || 'secondary'}
                  text={(LY_DO[chiTiet.lyDo] || {}).bg === 'warning' ? 'dark' : undefined}
                >
                  {(LY_DO[chiTiet.lyDo] || {}).nhan || chiTiet.lyDo}
                </Badge>
                <span className='ms-2 text-muted small'>
                  {(LY_DO[chiTiet.lyDo] || {}).giaiThich || ''}
                </span>
              </div>

              <Table size='sm' className='align-middle mb-0'>
                <thead className='table-light'>
                  <tr>
                    <th>Ngày</th>
                    <th>Giờ</th>
                    <th>Cabin</th>
                    <th>Trạng thái</th>
                  </tr>
                </thead>
                <tbody>
                  {(chiTiet.cacCa || []).map((c) => {
                    const n = nhanCa(c);
                    return (
                      <tr
                        key={c.uid}
                        className={c.laLichNay ? 'table-warning' : undefined}
                      >
                        <td className={c.laLichNay ? 'fw-semibold' : undefined}>{c.ngay || '—'}</td>
                        <td>{c.gio || '—'}</td>
                        <td className='small'>{c.cabin || '—'}</td>
                        <td>
                          <Badge bg={n.bg}>{n.nhan}</Badge>
                          {c.laLichNay ? (
                            <span className='ms-2 small fw-semibold'>← lịch đang đề nghị hủy</span>
                          ) : null}
                        </td>
                      </tr>
                    );
                  })}
                  {!(chiTiet.cacCa || []).length ? (
                    <tr>
                      <td colSpan={4} className='text-center text-muted py-3'>
                        {/* Không tra được ca nào KHÔNG đồng nghĩa với học viên chưa
                          * học ca nào — nói rõ để không ai suy ra sai. */}
                        Không đọc được ca nào cho mã này — đây không phải “học viên chưa học ca nào”.
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </Table>

              <Alert variant='light' className='border mt-3 mb-0 py-2 small'>
                Đã học <strong>{chiTiet.soCaDaHoc}</strong> ca (không tính ca vắng)
                {chiTiet.ngayCaCuoi ? ` · ca cuối ${chiTiet.ngayCaCuoi}` : ''}
                {chiTiet.soNgayCach === null || chiTiet.soNgayCach === undefined
                  ? ''
                  : ` · cách lịch này ${chiTiet.soNgayCach} ngày`}
                <div className='mt-1 text-muted'>
                  Danh sách gồm ca ở <strong>mọi cabin</strong> và <strong>cả ca đã hủy</strong>,
                  nên nó rộng hơn bảng cảnh báo bên ngoài. Việc đánh vắng
                  <strong> không phải lúc nào cũng được làm</strong>, nên một ca ghi
                  “Đã học” vẫn có thể là ca không ai đến.
                </div>
              </Alert>
            </>
          ) : null}
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setChiTiet(null)}>Đóng</Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
